import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Monitor, Smartphone, Tablet, Tv, Search, Filter, Plus, Check, AlertCircle, Settings, X } from "lucide-react";
import { Platform } from "@/types/profile";
import { payloads, getAllCategories } from "@/utils/payloads";
import { cn } from "@/lib/utils";
import { PayloadConfigForm } from "../PayloadConfigForm";

interface PlatformsAndPayloadsStepProps {
  selectedPlatforms: Platform[];
  onPlatformChange: (platforms: Platform[]) => void;
  selectedPayloads: string[];
  payloadConfigs: Record<string, Record<string, any>>;
  onSelectPayload: (payloads: string[]) => void;
  onConfigurePayload: (configs: Record<string, Record<string, any>>) => void;
  templatePayloads?: string[];
}

const platforms = [
  {
    id: "macos" as Platform,
    name: "macOS",
    description: "Desktop and laptop Macs",
    icon: Monitor,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
  },
  {
    id: "ios" as Platform,
    name: "iOS", 
    description: "iPhone devices",
    icon: Smartphone,
    color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
  },
  {
    id: "ipados" as Platform,
    name: "iPadOS",
    description: "iPad devices",
    icon: Tablet,
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
  },
  {
    id: "tvos" as Platform,
    name: "tvOS",
    description: "Apple TV devices",
    icon: Tv,
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300"
  }
];

export const PlatformsAndPayloadsStep = ({
  selectedPlatforms,
  onPlatformChange,
  selectedPayloads,
  payloadConfigs,
  onSelectPayload,
  onConfigurePayload,
  templatePayloads
}: PlatformsAndPayloadsStepProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [activePayload, setActivePayload] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-set first template payload as active
  useEffect(() => {
    if (templatePayloads && templatePayloads.length > 0 && !activePayload) {
      const firstPayload = templatePayloads[0];
      if (selectedPayloads.includes(firstPayload)) {
        setActivePayload(firstPayload);
      }
    }
  }, [templatePayloads, selectedPayloads, activePayload]);

  const handlePlatformToggle = (platformId: Platform, checked: boolean) => {
    if (!isMountedRef.current) return;
    
    if (checked) {
      onPlatformChange([...selectedPlatforms, platformId]);
    } else {
      onPlatformChange(selectedPlatforms.filter(p => p !== platformId));
    }
  };

  const handlePayloadToggle = (payloadId: string) => {
    if (!isMountedRef.current) return;
    
    if (selectedPayloads.includes(payloadId)) {
      onSelectPayload(selectedPayloads.filter(p => p !== payloadId));
      const newConfigs = { ...payloadConfigs };
      delete newConfigs[payloadId];
      onConfigurePayload(newConfigs);
      if (activePayload === payloadId) {
        setActivePayload(null);
      }
    } else {
      onSelectPayload([...selectedPayloads, payloadId]);
      setActivePayload(payloadId);
    }
  };

  const filteredPayloads = payloads.filter(payload => {
    // Filter by search query
    const matchesSearch = payload.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payload.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by category
    const matchesCategory = selectedCategory === "all" || payload.category === selectedCategory;
    
    // Filter by platform compatibility
    const isSupported = selectedPlatforms.length === 0 || 
                       payload.supportedPlatforms.some(platform => selectedPlatforms.includes(platform));
    
    return matchesSearch && matchesCategory && isSupported;
  });

  const categories = getAllCategories();
  const getPlatformLabel = (platform: Platform): string => {
    const platformLabels: Record<Platform, string> = {
      macos: "macOS",
      ios: "iOS", 
      ipados: "iPadOS",
      tvos: "tvOS"
    };
    return platformLabels[platform];
  };

  const getConfiguredFieldsCount = (payloadId: string): number => {
    const config = payloadConfigs[payloadId];
    if (!config) return 0;
    return Object.keys(config).filter(key => config[key] !== undefined && config[key] !== "").length;
  };

  const getTotalFieldsCount = (payloadId: string): number => {
    const payload = payloads.find(p => p.id === payloadId);
    return payload?.configFields.length || 0;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Platform Selection */}
      <div>
        <h2 className="text-2xl font-bold mb-2">Select Platforms</h2>
        <p className="text-muted-foreground mb-4">
          Choose which platforms this profile will support
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {platforms.map((platform) => {
            const Icon = platform.icon;
            const isSelected = selectedPlatforms.includes(platform.id);
            
            return (
              <Card 
                key={platform.id}
                disableHover
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  isSelected ? 'ring-2 ring-primary shadow-md' : ''
                )}
                onClick={() => handlePlatformToggle(platform.id, !isSelected)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", platform.color)}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <CardTitle className="text-sm">{platform.name}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {platform.description}
                        </p>
                      </div>
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payload Selection */}
      {selectedPlatforms.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-2">Configure Payloads</h2>
          <p className="text-muted-foreground mb-4">
            Select and configure the payloads for your profile
          </p>

          {/* Template indicator */}
          {templatePayloads && templatePayloads.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  Template
                </Badge>
                <span className="text-blue-700 dark:text-blue-300">
                  {templatePayloads.length} payload{templatePayloads.length !== 1 ? 's' : ''} added from template
                </span>
              </div>
            </div>
          )}

          <ResizablePanelGroup 
            key={`payload-panels-${selectedPlatforms.join('-')}`}
            direction="horizontal" 
            className="h-[600px] border rounded-lg"
          >
            {/* Left Panel - Two-Section Layout */}
            <ResizablePanel key="payloads-list" defaultSize={40} minSize={30}>
              <div className="h-full flex flex-col">
                {/* Search and Filter Header */}
                <div className="p-4 border-b space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search payloads..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-wrap gap-1">
                      <Button
                        variant={selectedCategory === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory("all")}
                        className="h-7 text-xs"
                      >
                        All
                      </Button>
                      {categories.map(category => (
                        <Button
                          key={category}
                          variant={selectedCategory === category ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedCategory(category)}
                          className="h-7 text-xs"
                        >
                          {category}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Selected Payloads Section */}
                {selectedPayloads.length > 0 && (
                  <div className="border-b">
                    <div className="px-4 py-2 bg-muted/50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">Selected Payloads</h3>
                        <Badge variant="secondary">{selectedPayloads.length}</Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onSelectPayload([]);
                          onConfigurePayload({});
                          setActivePayload(null);
                        }}
                        className="h-7 text-xs text-muted-foreground hover:text-destructive"
                      >
                        Clear All
                      </Button>
                    </div>
                    <ScrollArea className="max-h-[250px]">
                      <div className="p-4 space-y-2">
                        {selectedPayloads.map(payloadId => {
                          const payload = payloads.find(p => p.id === payloadId);
                          if (!payload) return null;
                          
                          const configuredCount = getConfiguredFieldsCount(payloadId);
                          const totalCount = getTotalFieldsCount(payloadId);
                          const isComplete = totalCount === 0 || configuredCount === totalCount;
                          
                          return (
                            <Card
                              key={payloadId}
                              disableHover
                              className={cn(
                                "cursor-pointer transition-all hover:shadow-sm",
                                activePayload === payloadId && "ring-2 ring-primary bg-muted/50"
                              )}
                              onClick={() => setActivePayload(payloadId)}
                            >
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-medium truncate">
                                        {payload.name}
                                      </span>
                                      {templatePayloads?.includes(payloadId) && (
                                        <Badge variant="secondary" className="text-xs">
                                          Template
                                        </Badge>
                                      )}
                                      {!isComplete && (
                                        <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <Badge variant="outline" className="text-xs">
                                        {payload.category}
                                      </Badge>
                                      {totalCount > 0 && (
                                        <span className={cn(
                                          isComplete ? "text-green-600" : "text-amber-600"
                                        )}>
                                          {configuredCount}/{totalCount} fields
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActivePayload(payloadId);
                                      }}
                                      className="h-7 w-7 p-0"
                                      title="Configure"
                                    >
                                      <Settings className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePayloadToggle(payloadId);
                                      }}
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                      title="Remove"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Available Payloads Section */}
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="px-4 py-2 bg-muted/30 border-b">
                    <h3 className="text-sm font-semibold">Available Payloads</h3>
                    <p className="text-xs text-muted-foreground">
                      {filteredPayloads.filter(p => !selectedPayloads.includes(p.id)).length} payload{filteredPayloads.filter(p => !selectedPayloads.includes(p.id)).length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                  <ScrollArea className="flex-1">
                    <div className="p-4 space-y-3">
                      {filteredPayloads.map(payload => {
                        const isSelected = selectedPayloads.includes(payload.id);
                        
                        // Skip already selected payloads in this section
                        if (isSelected) return null;
                        
                        return (
                          <Card 
                            key={payload.id}
                            disableHover
                            className="cursor-pointer transition-all hover:shadow-sm hover:ring-1 hover:ring-primary/50"
                            onClick={() => handlePayloadToggle(payload.id)}
                          >
                            <CardHeader className="pb-2">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    {payload.name}
                                    {payload.configFields.length > 0 && (
                                      <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                    )}
                                  </CardTitle>
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {payload.description}
                                  </p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2 h-7 w-7 p-0 flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePayloadToggle(payload.id);
                                  }}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-1">
                                  {payload.supportedPlatforms.map(platform => (
                                    <Badge
                                      key={platform}
                                      variant={selectedPlatforms.includes(platform) ? "default" : "outline"}
                                      className="text-xs px-1.5 py-0.5"
                                    >
                                      {getPlatformLabel(platform)}
                                    </Badge>
                                  ))}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {payload.category}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      
                      {filteredPayloads.filter(p => !selectedPayloads.includes(p.id)).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <Check className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">
                            {selectedPayloads.length > 0 
                              ? "All matching payloads have been added"
                              : "No payloads found matching your criteria"
                            }
                          </p>
                          {searchQuery || selectedCategory !== "all" ? (
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => {
                                setSearchQuery("");
                                setSelectedCategory("all");
                              }}
                              className="mt-2 text-xs"
                            >
                              Clear filters
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Right Panel - Payload Configuration */}
            <ResizablePanel key="payload-config" defaultSize={60} minSize={40}>
              <div className="h-full flex flex-col">
                {activePayload ? (
                  <div className="h-full flex flex-col">
                    <div className="p-4 border-b">
                      <h3 className="font-medium">Configure Payload</h3>
                      <p className="text-sm text-muted-foreground">
                        {payloads.find(p => p.id === activePayload)?.name}
                      </p>
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <ScrollArea className="h-full">
                        <div className="p-4">
                          <PayloadConfigForm
                            key={`config-${activePayload}`}
                            payloadId={activePayload}
                            initialValues={payloadConfigs[activePayload] || {}}
                            onValuesChange={(config) => {
                              if (isMountedRef.current) {
                                onConfigurePayload({
                                  ...payloadConfigs,
                                  [activePayload]: config
                                });
                              }
                            }}
                            onRemove={() => {
                              if (isMountedRef.current) {
                                handlePayloadToggle(activePayload);
                              }
                            }}
                          />
                        </div>
                      </ScrollArea>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-sm">
                      {selectedPayloads.length === 0 ? (
                        <>
                          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                          <h3 className="text-lg font-medium mb-2">No Payloads Selected</h3>
                          <p className="text-sm text-muted-foreground">
                            Select payloads from the available list to configure your profile
                          </p>
                        </>
                      ) : (
                        <>
                          <Settings className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                          <h3 className="text-lg font-medium mb-2">Select a Payload to Configure</h3>
                          <p className="text-sm text-muted-foreground">
                            Click on a selected payload to view and edit its configuration
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      )}
    </div>
  );
};